/**
 * User Data Service
 * Handles deletion of all user data for privacy compliance
 */
import { supabase, isSupabaseConfigured } from './supabaseClient';
import { signOut } from './authService';

/**
 * Delete all user data from Supabase
 * Deletes in correct order (child tables first to respect foreign keys)
 * @param {string} userId - The user's ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteAllUserData = async (userId) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Database not configured' };
  }

  if (!userId) {
    return { success: false, error: 'No user ID provided' };
  }

  try {
    // Delete in order (deepest child tables first to respect foreign keys)

    // --- Portfolio Tracker tables ---
    // 1a. CAS reconciliation logs
    const { error: casLogError } = await supabase
      .from('cas_reconciliation_log')
      .delete()
      .eq('user_id', userId);
    if (casLogError) console.warn('Error deleting cas_reconciliation_log:', casLogError.message);

    // 1b. User corporate actions (references family_members & corporate_actions)
    const { error: ucaError } = await supabase
      .from('user_corporate_actions')
      .delete()
      .eq('user_id', userId);
    if (ucaError) console.warn('Error deleting user_corporate_actions:', ucaError.message);

    // 1c. Upload logs
    const { error: uploadLogError } = await supabase
      .from('upload_logs')
      .delete()
      .eq('user_id', userId);
    if (uploadLogError) console.warn('Error deleting upload_logs:', uploadLogError.message);

    // 1d. Portfolio transactions (references holdings & family_members)
    const { error: txnError } = await supabase
      .from('portfolio_transactions')
      .delete()
      .eq('user_id', userId);
    if (txnError) console.warn('Error deleting portfolio_transactions:', txnError.message);

    // 1e. Portfolio holdings (references family_members)
    const { error: holdingsError } = await supabase
      .from('portfolio_holdings')
      .delete()
      .eq('user_id', userId);
    if (holdingsError) console.warn('Error deleting portfolio_holdings:', holdingsError.message);

    // 1f. Demat accounts (references family_members)
    const { error: dematError } = await supabase
      .from('demat_accounts')
      .delete()
      .eq('user_id', userId);
    if (dematError) console.warn('Error deleting demat_accounts:', dematError.message);

    // 1g. Family members (parent table for holdings/transactions)
    const { error: familyError } = await supabase
      .from('family_members')
      .delete()
      .eq('user_id', userId);
    if (familyError) console.warn('Error deleting family_members:', familyError.message);

    // --- Legacy planning tables ---
    // 2. Investments (references accounts and goals)
    const { error: investmentsError } = await supabase
      .from('investments')
      .delete()
      .eq('user_id', userId);
    if (investmentsError) console.warn('Error deleting investments:', investmentsError.message);

    // 3. Accounts
    const { error: accountsError } = await supabase.from('accounts').delete().eq('user_id', userId);
    if (accountsError) console.warn('Error deleting accounts:', accountsError.message);

    // 4. Goals
    const { error: goalsError } = await supabase.from('goals').delete().eq('user_id', userId);
    if (goalsError) console.warn('Error deleting goals:', goalsError.message);

    // 5. User Profile
    const { error: profileError } = await supabase
      .from('user_profile')
      .delete()
      .eq('user_id', userId);
    if (profileError) console.warn('Error deleting user_profile:', profileError.message);

    // 6. Saved Plans
    const { error: plansError } = await supabase.from('saved_plans').delete().eq('user_id', userId);
    if (plansError) console.warn('Error deleting saved_plans:', plansError.message);

    // 7. Profiles table
    const { error: userProfilesError } = await supabase.from('profiles').delete().eq('id', userId);
    if (userProfilesError) console.warn('Error deleting profiles:', userProfilesError.message);

    // Clear all localStorage data
    localStorage.clear();

    // Clear all sessionStorage data
    sessionStorage.clear();

    // Sign out the user
    await signOut();

    return { success: true };
  } catch (error) {
    console.error('Error deleting user data:', error);
    return { success: false, error: error.message };
  }
};
